"use client";

/** Advisory warning shown when a user-picked Marketplace product looks like a
 *  poor fit for the intake. Purely presentational and purely advisory — it
 *  offers an escape hatch but never gates the flow. */

import { Alert } from "../lib/icons";

export interface ProductFitWarningProps {
  productName: string;
  reason: string;
  concerns: string[];
  onPickAnother: () => void;
}

export function ProductFitWarning({
  productName,
  reason,
  concerns,
  onPickAnother,
}: ProductFitWarningProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-[15px] mb-[13px] flex gap-2.5 rounded-(--radius-field) border border-(--status-warning) bg-(--status-warning-bg) px-[13px] py-[11px] font-[family-name:var(--font-body)]"
    >
      <span
        aria-hidden="true"
        className="mt-0.5 shrink-0 text-(--status-warning-text)"
      >
        <Alert size={15} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-(--status-warning-text)">
          This may not fit your intake
        </p>
        <p className="mt-1 text-[12.5px] leading-[1.45] text-(--status-warning-text)">
          {reason}
        </p>
        {concerns.length > 0 && (
          <ul className="mt-1.5 flex flex-col gap-0.5">
            {concerns.map((concern, index) => (
              <li
                key={`${concern}-${index}`}
                className="text-[12px] text-(--status-warning-text) before:mr-1.5 before:content-['•']"
              >
                {concern}
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={onPickAnother}
          className="mt-2 cursor-pointer border-0 bg-transparent p-0 text-[12.5px] font-semibold text-(--status-warning-text) underline underline-offset-2 hover:no-underline"
        >
          Pick another
        </button>
      </div>
      <span className="sr-only">{productName}</span>
    </div>
  );
}
